import qs from "qs";
import { ComponentPropsWithRef } from "react";
import { useTranslation } from "react-i18next";

import { IClassroomResource, IUserResource, getResourceUuid, getUserDisplayName, useApi } from "@sol/sdk";
import { RelationalValue } from "@sol/sdk/utils/filters";
import { notification } from "@sol/uikit/feedback/Notification/Notification";

import { DownloadButton, DownloadError, downloadBlobFromUrl, useDownload } from "@/shared/download";

import { sanitizeFilename } from "./utils";

type DownloadVariables = { url: string; filename: string };

type Props = {
    learner: IUserResource;
    classroom?: RelationalValue<IClassroomResource>;
} & Omit<ComponentPropsWithRef<typeof DownloadButton>, "href" | "isPending" | "progress" | "onClick">;

export function LearnerResultsDownloadButton({ learner, classroom, children, download, ...rest }: Props) {
    const api = useApi();
    const [t] = useTranslation();
    const {
        download: triggerDownload,
        isPending,
        progress,
    } = useDownload<DownloadVariables, void, DownloadError>(({ url, filename }, { onProgress }) =>
        downloadBlobFromUrl(url, filename, {
            client: api,
            config: { baseURL: "" },
            onProgress,
        }),
    );

    const fileName = sanitizeFilename(
        download ??
            t("component.shared.LearnerResultsDownloadButton.filename", {
                date: new Date(),
                displayName: getUserDisplayName(learner),
            }),
    );

    const query = qs.stringify({
        classroomUUID: classroom && getResourceUuid(classroom),
    });
    const url = `/api/users/${getResourceUuid(learner)}/classroom-results${query ? `?${query}` : ""}`;

    const onClick = () => {
        triggerDownload(
            { url, filename: fileName },
            {
                onError: error =>
                    notification.error({
                        message: t("component.follow-ups.list.LearnersList.error"),
                        description: error.apiMessage,
                    }),
            },
        );
    };

    return (
        <DownloadButton onClick={onClick} isPending={isPending} progress={progress} {...rest}>
            {children ?? t("component.follow-ups.list.LearnersList.downloadReport")}
        </DownloadButton>
    );
}
